import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  ArrowPathIcon,
  LockClosedIcon,
  LockOpenIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import AuthContext from "../../../context/assessment/AuthContext";
import LoaderContext from "../../../context/assessment/LoaderContext";
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import WebSocketContext from "../../../context/WebSocketContext";
import Assessment from "../../../controller/assessment/assessment_system/assessment";
import errorHandler from "../../../helper/assessment/errorHandler";
import {
  IntegritySummaryStrip,
  ParticipantIntegrityState,
  useIntegrityLive,
} from "../../../components/shared/sessionIntegrity/admin";

function FormParticipantSessionPanel(props: {
  doc: TAssessment;
  assessorOptions: TSelect[];
  isOpenParticipantSessionPanel: TAssessmentSession | undefined;
  setIsOpenParticipantSessionPanel: (close: undefined) => void;
}) {
  const { user } = useContext(AuthContext);
  const { message, setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const { sendMessage } = useContext(WebSocketContext);
  const [participantSessionList, setParticipantSessionList] = useState<TAssessmentParticipantSession[]>([]);
  const [updatingId, setUpdatingId] = useState<number>();
  const controlLockRef = useRef(false);
  const openedSessionId = props.isOpenParticipantSessionPanel?.id;
  const loadIntegrity = useCallback((assessmentId: string) => {
    const controller = new Assessment();
    return controller.getIntegrityLive(assessmentId, openedSessionId);
  }, [openedSessionId]);
  const integrity = useIntegrityLive(props.doc.id, loadIntegrity);

  useEffect(() => {
    if (!openedSessionId) return;
    void integrity.refresh();
  }, [openedSessionId, integrity.refresh]);

  const isAllowed =
    user.roles === "1" || props.doc.pic_id.toString() === user.assessor_id;

  const getDoc = useCallback(async () => {
    try {
      const ac = new Assessment();
      const response = await ac.listParticipantSession(props.doc.id.toString());
      setParticipantSessionList(
        response.data.list.filter(
          (item: TAssessmentParticipantSession) =>
            item.assessment_session_id === props.isOpenParticipantSessionPanel?.id
        )
      );
    } catch (error) {
      setNotif({ type: "error", message: errorHandler(error) });
    }
  }, [
    props.doc.id,
    props.isOpenParticipantSessionPanel?.id,
    setNotif,
  ]);

  useEffect(() => {
    getDoc();
  }, [getDoc]);

  const sortedSessions = useMemo(
    () =>
      [...participantSessionList].sort((a, b) =>
        (a.full_name ?? "").localeCompare(b.full_name ?? "")
      ),
    [participantSessionList]
  );

  const notifyParticipants = (opened: boolean) => {
    sendMessage(
      "assessment",
      JSON.stringify({
        target: "participant",
        action: "check open session",
        message: opened
          ? "Assessor membuka sesi. Halaman diperbarui."
          : "Assessor menutup sesi. Mohon tunggu arahan berikutnya.",
      })
    );
  };

  const controlSession = async (
    participantSession: TAssessmentParticipantSession,
    open: boolean,
    resetClock = false
  ) => {
    if (!participantSession.id || controlLockRef.current) return;
    controlLockRef.current = true;
    try {
      setUpdatingId(participantSession.id);
      setMessage(open ? "Membuka sesi" : "Menutup sesi");
      const ac = new Assessment();
      await ac.controlParticipantSession(
        participantSession.id.toString(),
        open,
        resetClock
      );
      await getDoc();
      await integrity.refresh();
      notifyParticipants(open);
      setNotif({
        type: "success",
        message: open
          ? resetClock
            ? "Sesi dibuka dengan waktu baru."
            : "Sesi dibuka. Data sebelumnya tetap tersimpan."
          : "Sesi ditutup.",
      });
    } catch (error) {
      setNotif({ type: "error", message: errorHandler(error) });
    } finally {
      controlLockRef.current = false;
      setUpdatingId(undefined);
      setMessage("");
    }
  };

  const confirmControl = async (
    participantSession: TAssessmentParticipantSession,
    open: boolean
  ) => {
    if (!participantSession.id || controlLockRef.current || updatingId) return;
    const confirmation = await Swal.fire({
      title: open ? "Buka sesi?" : "Tutup sesi?",
      text: open
        ? "Peserta bisa mulai mengerjakan. Sesi lain milik peserta yang sama akan ditutup."
        : "Peserta tidak bisa melanjutkan sampai sesi dibuka lagi.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: open ? "Buka sesi" : "Tutup sesi",
      cancelButtonText: "Batal",
      confirmButtonColor: open ? "#0891b2" : "#475569",
    });
    if (confirmation.isConfirmed) {
      await controlSession(participantSession, open);
    }
  };

  const confirmReset = async (
    participantSession: TAssessmentParticipantSession
  ) => {
    if (!participantSession.id || controlLockRef.current || updatingId) return;
    const confirmation = await Swal.fire({
      title: "Buka dengan waktu baru?",
      text: "Jawaban tetap tersimpan, tetapi waktu mulai dan waktu selesai akan diatur ulang.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Buka dan atur ulang",
      cancelButtonText: "Batal",
      confirmButtonColor: "#0891b2",
    });
    if (confirmation.isConfirmed) {
      await controlSession(participantSession, true, true);
    }
  };

  return (
    <div>
      <div className="fixed inset-0 z-20 bg-black opacity-20" />
      <div className="fixed inset-0 z-30 flex items-center justify-center">
        <div className="h-full w-screen overflow-hidden bg-white md:h-[96%] md:w-[96%] md:rounded-lg">
          <div className="flex items-center justify-between bg-cyan-800 p-4 text-white">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">
                  Kontrol sesi peserta
                </h3>
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-xs">
                  {props.isOpenParticipantSessionPanel?.timing_mode ===
                  "admin_controlled"
                    ? "Dikontrol assessor"
                    : "Dengan batas waktu"}
                </span>
              </div>
              <p className="mt-1 text-sm text-cyan-100">
                {props.isOpenParticipantSessionPanel?.category} ·{" "}
                {props.isOpenParticipantSessionPanel?.description}
              </p>
            </div>
            <button
              type="button"
              aria-label="Tutup panel"
              onClick={() => props.setIsOpenParticipantSessionPanel(undefined)}
            >
              <XMarkIcon className="w-6" />
            </button>
          </div>
          <div className="h-0.5">
            {message !== "" && (
              <div className="h-full animate-loader bg-gradient-to-r from-cyan-300 to-blue-700" />
            )}
          </div>
          <div className="h-[calc(100%_-_5rem)] overflow-auto p-4">
            <p className="mb-4 text-sm text-gray-600">
              Saat sesi dibuka, sesi lain milik peserta yang sama akan ditutup otomatis.
            </p>
            <IntegritySummaryStrip
              summary={integrity.summary}
              available={integrity.available}
            />
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 px-4">
              {sortedSessions.map((participantSession) => {
                const isOpen = participantSession.control_status === "open";
                const isUpdating = updatingId === participantSession.id;
                return (
                  <li
                    key={participantSession.id}
                    className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {participantSession.full_name}
                        </p>
                        <span
                          className={
                            "rounded-full px-2 py-0.5 text-xs font-medium " +
                            (isOpen
                              ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                              : "bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-500/10")
                          }
                        >
                          {isOpen ? "Dibuka" : "Ditutup"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {participantSession.timing_mode === "admin_controlled"
                          ? "Berjalan sampai assessor menutup sesi"
                          : participantSession.end_time
                          ? `Waktu selesai: ${participantSession.end_time}`
                          : "Waktu dimulai saat peserta menekan Mulai"}
                      </p>
                      <div className="mt-2">
                        <ParticipantIntegrityState
                          row={
                            participantSession.id
                              ? integrity.list.find(
                                  (row) =>
                                    row.participant_id ===
                                      participantSession.assessment_participant_id &&
                                    row.session_id === participantSession.id
                                )
                              : undefined
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={!isAllowed || isUpdating}
                        onClick={() =>
                          void confirmControl(participantSession, !isOpen)
                        }
                        className={
                          "inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300 " +
                          (isOpen
                            ? "bg-slate-600 hover:bg-slate-700"
                            : "bg-cyan-600 hover:bg-cyan-700")
                        }
                      >
                        {isOpen ? (
                          <LockClosedIcon className="mr-1.5 w-4" />
                        ) : (
                          <LockOpenIcon className="mr-1.5 w-4" />
                        )}
                        {isUpdating
                          ? isOpen
                            ? "Menutup..."
                            : "Membuka..."
                          : isOpen
                          ? "Tutup sesi"
                          : "Buka sesi"}
                      </button>
                      {participantSession.timing_mode === "timed" &&
                        participantSession.start_time && (
                          <button
                            type="button"
                            disabled={!isAllowed || isUpdating}
                            onClick={() => confirmReset(participantSession)}
                            className="inline-flex items-center rounded-md border border-cyan-600 px-3 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                          >
                            <ArrowPathIcon className="mr-1.5 w-4" />
                            Buka dengan waktu baru
                          </button>
                        )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {sortedSessions.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-500">
                Belum ada peserta pada simulasi ini.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormParticipantSessionPanel;
